/**
 * DramaPlusPreview Component
 *
 * Feature preview card showing what Drama+ users unlock.
 * Displays a compact teaser of premium features with an upgrade CTA.
 * Used inside GatedSection blur overlays on the results screen.
 *
 * Mobile-first: 375px minimum width, 44x44px touch targets.
 *
 * @see DD-157 - Drama+ Conversion UX
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { GlassButton } from '../../glass/GlassButton';
import { createBoxShadow } from '../../../lib/design-tokens';

interface FeatureItem {
  /** Icon emoji or symbol */
  icon: string;
  /** Feature name */
  title: string;
  /** Brief description */
  description: string;
}

export interface DramaPlusPreviewProps {
  /** Callback when user taps upgrade CTA */
  onUpgrade: () => void;
  /** Optional test ID for E2E testing */
  testID?: string;
}

export function DramaPlusPreview({
  onUpgrade,
  testID = 'drama-plus-preview',
}: DramaPlusPreviewProps) {
  const { t } = useTranslation('features');

  const FEATURES: FeatureItem[] = [
    {
      icon: '\u{1F50D}',
      title: t('gating.feature_preview_title_patterns', {
        defaultValue: 'Pattern Spotlight',
      }),
      description: t('gating.feature_preview_desc_patterns', {
        defaultValue: 'See the communication pattern this message may repeat.',
      }),
    },
    {
      icon: '\u{1F4CA}',
      title: t('gating.feature_preview_title_rewrite_help', {
        defaultValue: 'Better Rewrite Help',
      }),
      description: t('gating.feature_preview_desc_rewrite_help', {
        defaultValue:
          'Get clearer suggestions to soften tone and improve this message before you send.',
      }),
    },
    {
      icon: '\u{1F4DD}',
      title: t('gating.feature_preview_title_annotations', {
        defaultValue: 'Inline Annotations',
      }),
      description: t('gating.feature_preview_desc_annotations', {
        defaultValue:
          'See what to change directly in the message, right where it lands.',
      }),
    },
  ];

  return (
    <View style={styles.container} testID={testID}>
      <View style={styles.card} testID={`${testID}-card`}>
        {/* Header */}
        <Text style={styles.badge}>
          {t('gating.drama_plus_badge', { defaultValue: 'Drama+' })}
        </Text>
        <Text style={styles.title}>
          {t('gating.preview_title', {
            defaultValue: 'Get clearer rewrite help before you send',
          })}
        </Text>
        <Text style={styles.subtitle}>
          {t('gating.preview_subtitle', {
            defaultValue:
              'Drama+ adds better rewrite guidance, inline notes, and pattern spotting. DramaX goes deeper with transformation plans.',
          })}
        </Text>

        {/* Feature list */}
        <View style={styles.featureList}>
          {FEATURES.map((feature, index) => (
            <View
              key={feature.title}
              style={styles.featureRow}
              testID={`${testID}-feature-${index}`}
            >
              <Text style={styles.featureIcon}>{feature.icon}</Text>
              <View style={styles.featureText}>
                <Text style={styles.featureName}>{feature.title}</Text>
                <Text style={styles.featureDesc}>{feature.description}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Upgrade CTA -- 44px minimum touch target */}
        <GlassButton
          onPress={onUpgrade}
          accessibilityRole="button"
          accessibilityLabel={t('gating.upgrade_to_tier', {
            tierName: 'Drama+',
            defaultValue: 'Upgrade to Drama+',
          })}
          testID={`${testID}-upgrade-button`}
        >
          {t('gating.try_drama_plus', {
            defaultValue: 'Try Drama+ rewrite help',
          })}
        </GlassButton>
      </View>
    </View>
  );
}

// Platform-aware card shadow
const cardShadow = createBoxShadow({
  color: '#7c3aed',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 20,
  opacity: 0.15,
  elevation: 6,
});

const styles = StyleSheet.create({
  container: {
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    maxWidth: 340,
    width: '100%',
    ...cardShadow,
  } as ViewStyle,
  badge: {
    fontSize: 12,
    fontWeight: '700',
    color: '#7c3aed',
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a2e',
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  featureList: {
    width: '100%',
    marginBottom: 20,
    gap: 12,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 44, // 44px touch target
  },
  featureIcon: {
    fontSize: 24,
    width: 32,
    textAlign: 'center',
  },
  featureText: {
    flex: 1,
  },
  featureName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1a1a2e',
  },
  featureDesc: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 18,
  },
});
